import clsx from 'clsx';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { ClientVacationPeriod } from '../../../../../models/vacationClientView.types';
import type { ConflictPersonItem } from '../clientPeriodCard.types';
import { formatPeriodRange } from '../../../vacationClientPanel.definitions';
import { ConflictPersonRow } from './ConflictPersonRow';
import { VacationAvatar } from '../../../../VacationDpTable/components/VacationAvatar';
import { PANEL_HEADER_HEIGHT, RULER_HEIGHT, ROW_HEIGHT } from '../clientPeriodCard.definitions';
import { CollaboratorNameLink } from '../../CollaboratorNameLink';

interface ConflictPeoplePanelProps {
    period: ClientVacationPeriod;
    people: ConflictPersonItem[];
}

export function ConflictPeoplePanel({ period, people }: ConflictPeoplePanelProps) {
    if (people.length === 0) {
        return null;
    }

    const distinctPeopleCount = new Set(people.map(p => p.collaboratorId)).size;

    return (
        <div className="rounded-xl border border-amber-100 overflow-hidden">
            <div className={clsx('flex items-center justify-between px-4 bg-amber-50 border-b border-amber-100', PANEL_HEADER_HEIGHT)}>
                <h4 className="text-sm font-semibold text-amber-800 flex items-center gap-1">
                    <ExclamationTriangleIcon className="h-4 w-4" />
                    Dias em conflito ({distinctPeopleCount} {distinctPeopleCount === 1 ? 'pessoa' : 'pessoas'})
                </h4>
            </div>

            <div className="px-4">
                    <div className={clsx('flex flex-col bg-amber-50/40 border-b border-amber-100', RULER_HEIGHT)}>
                        <div className="flex items-center gap-3 flex-1 border-b border-amber-100">
                            <div className="w-10" />
                            <div className="flex-1">
                                <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-700/80">Colaborador</span>
                            </div>
                            <div className="w-24 text-right">
                                <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-700/80">Dias</span>
                            </div>
                        </div>
                    </div>

                    <div className={clsx('flex items-center gap-3 border-b border-raro-gray-100 bg-primary-50/40', ROW_HEIGHT)}>
                        <VacationAvatar name={period.collaboratorName} photoUrl={period.collaboratorPhotoUrl} />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-raro-gray-900 truncate">
                                <CollaboratorNameLink collaboratorId={period.collaboratorId} name={period.collaboratorName} />
                            </p>
                            <p className="text-xs text-raro-gray-500">{formatPeriodRange(period.startDate, period.endDate)}</p>
                        </div>
                        <span className="text-xs font-semibold px-2 py-1 rounded whitespace-nowrap bg-primary-100 text-primary-800">
                            {period.totalDays}d
                        </span>
                    </div>

                    {people.map((person) => (
                        <div key={person.key} className={clsx('border-b border-raro-gray-100', ROW_HEIGHT, 'flex flex-col justify-center')}>
                            <ConflictPersonRow person={person} period={period} />
                        </div>
                    ))}
            </div>
        </div>
    );
}
